import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import NoRecordFoundComponent from "@/app/components/ui/NoRecordFound";
import OverbookingConfirmationModal from "@/app/components/routes/modals/OverbookingConfirmationModal";
import SimplePageHeading from "@/app/components/ui/PageHeadings/SimplePageHeading";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { format, isBefore, startOfDay } from "date-fns";
import { useEffect, useState, useRef, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { FunnelX, Download, Link as LinkIcon } from "lucide-react";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { useNavigate } from "react-router";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useRotaSlotRemovalsListAPI } from "@/app/hooks/api_hooks/Rota/useRotaSlotRemovalsListAPI";
import { useRotaSlotRemovalsStaffAPI } from "@/app/hooks/api_hooks/Rota/useRotaSlotRemovalsStaffAPI";
import { useRotaSlotRemovalCreateAPI } from "@/app/hooks/api_hooks/Rota/useRotaSlotRemovalCreateAPI";
import { useRotaNumberOfSlotRemoved } from "@/app/hooks/api_hooks/Rota/useRotaNumberOfSlotRemoved";
import { useRotaSlotRemovalsClinicStaffAPI } from "@/app/hooks/api_hooks/Rota/useRotaSlotRemovalsClinicStaffAPI";
import { useRotaSlotRemovalsBulkUploadAPI } from "@/app/hooks/api_hooks/Rota/useRotaSlotRemovalsBulkUploadAPI";
import { IRotaSlotRemovalData, IRotaSlotRemovalStaffData, IRotaSlotRemovalClinicStaffData } from "@/api_framework/api_modals/Rota";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

interface IRemoveSlotsFormFields {
  clinicId: string;
  clinicName: string;
  providerId: string;
  providerName: string;
  date: string;
  subservices: ISelectMenuItemData[];
  startTimeHour: string;
  startTimeMinute: string;
  endTimeHour: string;
  endTimeMinute: string;
  shift?: string;
}

const EMPTY_FILTERS: IRemoveSlotsFormFields = {
  clinicId: "",
  clinicName: "",
  providerId: "",
  providerName: "",
  date: "",
  subservices: [],
  startTimeHour: "",
  startTimeMinute: "",
  endTimeHour: "",
  endTimeMinute: "",
};

const TABLE_HEADERS = [
  { item: "Date" },
  { item: "Clinic" },
  { item: "Provider" },
  { item: "Sub Services" },
  { item: "Start Time" },
  { item: "End Time" },
  { item: "Removed By" },
];

const FormRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-6 py-4 border-b border-gray-50 last:border-b-0">
    <span className="w-[148px] shrink-0 text-xs font-medium text-[#1F1F23] pt-[9px] leading-tight">
      {label}
    </span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);
export const RemoveSlotsCreateRequestRoute = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const listUrl = getRouteKey("REMOVE_SLOTS", "url");

  return (
    <RemoveSlotsCreatePage
      onCancel={() => navigate(listUrl)}
      onSaved={() => navigate(listUrl)}
    />
  );
};


const RemoveSlotsCreatePage = ({ onCancel, onSaved }: { onCancel: () => void; onSaved: () => void }) => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ROTA", "id");
  const { routeState } = useRouteInfo(routeId);

  const clinics = routeState?.state?.clinics ?? [];

  const fetchCities = useOrgCitiesListAPI("ROTA");
  const { execute: fetchStaffList, loading: isStaffLoading } = useRotaSlotRemovalsStaffAPI();
  const { execute: createSlotRemoval } = useRotaSlotRemovalCreateAPI();
  const { execute: getNumberOfSlotsRemoved } = useRotaNumberOfSlotRemoved();

  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState("");
  const [fetchedStaffList, setFetchedStaffList] = useState<IRotaSlotRemovalStaffData[]>([]);

  const arrayConstants = useArrayConstants();

  const { execute: logAPI } = useClickLogAPI();
  const loggedInUser = useAppSelector((state) => state.auth?.user);
  const vmsUserId = loggedInUser?.email ?? "";
  const userId = useAppSelector((state) => state?.auth?.userLoginInfo?.user_id);

  const sendClickLog = (eventType: string, ctaText: string) => {
    logAPI({
      event_type: eventType,
      scr_id: "REMOVE_SLOTS_CREATE",
      timestamp: Date.now(),
      user_id: userId,
      vms_user_id: vmsUserId,
      cta_text: ctaText,
    });
  };
  const startTimeItem = (arrayConstants.overBookingStartTimeItems ?? []) as ISelectMenuItemData[];
  const startTimeMinuteItem = (arrayConstants.overBookingStartTimeMinuteItems ?? []) as ISelectMenuItemData[];

  const formHook = useForm<IRemoveSlotsFormFields>({
    mode: "onChange",
    defaultValues: {
      clinicId: "",
      clinicName: "",
      providerId: "",
      providerName: "",
      date: format(new Date(), "yyyy-MM-dd"),
      subservices: [],
      startTimeHour: "",
      startTimeMinute: "",
      endTimeHour: "",
      endTimeMinute: "",
      shift: "",
    },
  });

  const clinicItems = useSelectMenuReducer(clinics, "display_name", "id");

  const selectedClinicId = formHook.watch("clinicId");
  const selectedClinicName = formHook.watch("clinicName");
  const selectedProviderId = formHook.watch("providerId");
  const selectedDate = formHook.watch("date");

  const providerItems = fetchedStaffList.map((item) => ({
    id: item.staff_id,
    title: item.staff_name,
  }));

  const selectedProviderObj = fetchedStaffList.find(
    (p) => p.staff_id === selectedProviderId
  );

  const subserviceItems = (selectedProviderObj?.sub_services || []) as any;

  useEffect(() => {
    fetchCities({
      withClinics: true,
      withClinicProviders: false,
      clinicOptions: { selectAll: true },
    });
  }, []);

  useEffect(() => {
    formHook.setValue("providerId", "");
    formHook.setValue("providerName", "");
    formHook.setValue("subservices", []);
    setFetchedStaffList([]);

    if (!selectedClinicId || !selectedDate) {
      return;
    }

    fetchStaffList({ clinic_id: selectedClinicId, date: selectedDate }).then((data) => {
      (data || []).forEach((staff) => {
        (staff.sub_services || []).forEach((sub: any) => {
          sub.id = sub.sub_service_id;
          sub.title = sub.sub_service_name;
        });
      });
      setFetchedStaffList(data);
    });
  }, [selectedClinicId, selectedDate]);

  useEffect(() => {
    formHook.setValue("subservices", []);
  }, [selectedProviderId]);

  useEffect(() => {
    if (!selectedClinicId || selectedClinicName) return;
    const matchedClinic = clinicItems?.find((item) => item.id === selectedClinicId);
    if (matchedClinic?.title) {
      formHook.setValue("clinicName", matchedClinic.title, { shouldValidate: true });
    }
  }, [selectedClinicId, clinicItems]);

  const handleSave = async () => {
    sendClickLog("REMOVE_SLOTS_CREATE_SAVE", "Save");
    const values = formHook.getValues();
    if (
      !values.clinicId ||
      !values.date ||
      !values.providerId ||
      !values.subservices ||
      values.subservices.length === 0 ||
      !values.startTimeHour ||
      !values.startTimeMinute ||
      !values.endTimeHour ||
      !values.endTimeMinute
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsSaving(true);
    const subServiceIds = values.subservices.map((sub) => sub.id);

    const impactRes = await getNumberOfSlotsRemoved({
      staff_id: values.providerId,
      date: values.date,
      start_time: `${values.startTimeHour}:${values.startTimeMinute}`,
      end_time: `${values.endTimeHour}:${values.endTimeMinute}`,
      sub_service_ids: subServiceIds,
    });

    setIsSaving(false);

    const apiMsg = impactRes?.message;
    setConfirmModalMessage(apiMsg);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    const values = formHook.getValues();
    const subServiceIds = values.subservices.map((sub) => sub.id);

    const response = await createSlotRemoval({
      staff_id: values.providerId,
      clinic_id: values.clinicId,
      date: values.date,
      sub_service_ids: subServiceIds,
      start_time: `${values.startTimeHour}:${values.startTimeMinute}`,
      end_time: `${values.endTimeHour}:${values.endTimeMinute}`,
      confirmed: true,
    });

    setIsSaving(false);
    setIsConfirmModalOpen(false);
    if (response?.status) {
      toast.success(response.message || "Slot removal request initiated successfully");
      onSaved();
    }
  };

  return (
    <div className="bg-white px-4 py-4 pb-8 min-h-screen">
      <div className="mx-auto w-full">
        <SimplePageHeading
          title="Create Slot Removal Request"
          description="Select clinic, date, provider, subservices, and shift timings to remove slots."
        />
        <div className="w-full lg:w-2/4 mt-4 mx-auto border border-gray-50 shadow-lg rounded-lg p-6 bg-white">
          <FormRow label="Select Clinic">
            <input hidden {...formHook.register("clinicId", { required: true })} />
            <SearchableSelectMenu
              errorMessages={[{ message: "Clinic is required", type: "required" }]}
              onSelectItem={(item) => {
                formHook.setValue("clinicId", item?.id ?? "");
                formHook.setValue("clinicName", item?.title ?? "");
                sendClickLog("REMOVE_SLOTS_CREATE_SELECT_CLINIC", item?.title ?? "Select Clinic");
              }}
              fieldError={formHook.formState.errors.clinicName}
              register={formHook.register("clinicName", { required: true })}
              selectItems={clinicItems ?? []}
              placeholder="Select Clinic"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon
              defaultSelected={clinicItems?.find((item) => item.id === selectedClinicId)}
              listBoxClassName="absolute mt-1 overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none max-h-60 sm:text-sm w-full z-[80]"
              className="text-xs text-gray-700"
              containerClassName="w-full"
              showDropdownWhenNoData
            />
          </FormRow>
          <FormRow label="Select Date">
            <PWLCalendarComponent
              errorMessages={[{ message: "Date is required", type: "required" }]}
              register={formHook.register("date", { required: true })}
              fieldError={formHook.formState.errors.date}
              showTypedErrors={false}
              showLeftNav={false}
              allowPastDaySelection={false}
              disablePastMonthSelection
              disableDatesBeforeDate={format(new Date(), "yyyy-MM-dd")}
              defaultSelectedDate={selectedDate}
              onSelectDate={(date) => {
                if (!date || isBefore(startOfDay(date), startOfDay(new Date()))) return;
                const formattedDate = format(date, "yyyy-MM-dd");
                formHook.setValue("date", formattedDate);
                sendClickLog("REMOVE_SLOTS_CREATE_SELECT_DATE", formattedDate);
              }}
              className="w-full py-2 text-sm focus:outline-none !focus:ring-0 !ring-gray-300"
            />
          </FormRow>
          <FormRow label="Select Provider">
            <input hidden {...formHook.register("providerId", { required: true })} />
            <SearchableSelectMenu
              errorMessages={[{ message: "Provider is required", type: "required" }]}
              onSelectItem={(item) => {
                formHook.setValue("providerId", item?.id ?? "");
                formHook.setValue("providerName", item?.title ?? "");
                sendClickLog("REMOVE_SLOTS_CREATE_SELECT_PROVIDER", item?.title ?? "Select Provider");
              }}
              fieldError={formHook.formState.errors.providerName}
              register={formHook.register("providerName", { required: true })}
              selectItems={providerItems}
              placeholder={
                isStaffLoading
                  ? "Loading Providers..."
                  : selectedClinicId && selectedDate
                    ? "Select Provider"
                    : "Please select clinic and date first"
              }
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon
              defaultSelected={providerItems?.find((item) => item.id === selectedProviderId)}
              listBoxClassName="absolute mt-1 overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none max-h-60 sm:text-sm w-full z-[80]"
              className="text-xs text-gray-700"
              containerClassName="w-full"
              showDropdownWhenNoData
            />
          </FormRow>

          <FormRow label="Select Subservice">
            <input hidden {...formHook.register("subservices", { required: true })} />
            <div
              onKeyDownCapture={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              <SearchableMultiSelectMenu
                key={selectedProviderId}
                withSelectAll
                withDropDownSearch={true}
                errorMessages={[{ message: "Subservices are required", type: "required" }]}
                onSelectItem={(items) => {
                  formHook.setValue("subservices", items ?? []);
                  sendClickLog("REMOVE_SLOTS_CREATE_SELECT_SUBSERVICE", items?.map(i => i.title).join(", ") || "Select Subservice");
                }}
                fieldError={formHook.formState.errors.subservices}
                register={formHook.register("subservices", { required: true })}
                selectItems={subserviceItems}
                placeholder={
                  selectedProviderId
                    ? "Select Subservices"
                    : "Please select provider first"
                }
                showTooltips={false}
                showTypedErrors={false}
                showDropdownIcon
                listBoxClassName="w-full z-[80]"
                className="text-xs text-gray-700"
                containerClassName="w-full"
              />
            </div>
          </FormRow>

          <FormRow label="Timings">
            <div className="space-y-3 w-full">
              <div className="flex sm:flex-row flex-col gap-4">
                <div className="flex-1 space-y-1">
                  <p className="text-xs font-semibold text-[#1F1F23]">Start Time</p>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <NonSearchableSelectMenu
                        errorMessages={[{ message: "Hour is required", type: "required" }]}
                        onSelectItem={(item) => {
                          formHook.setValue("startTimeHour", item?.title ?? "");
                          sendClickLog("REMOVE_SLOTS_CREATE_START_HOUR", item?.title ?? "Start Hour");
                        }}
                        fieldError={formHook.formState.errors.startTimeHour}
                        register={formHook.register("startTimeHour")}
                        selectItems={startTimeItem}
                        placeholder="HH"
                        showTooltips={false}
                        showTypedErrors={false}
                        showDropdownIcon
                        defaultSelected={startTimeItem?.find((item) => item.title === formHook.watch("startTimeHour"))}
                        listBoxClassName="w-full z-[70]"
                        className="text-xs text-gray-700"
                        containerClassName="w-full"
                      />
                    </div>
                    <div className="flex-1">
                      <NonSearchableSelectMenu
                        errorMessages={[{ message: "Minute is required", type: "required" }]}
                        onSelectItem={(item) => {
                          formHook.setValue("startTimeMinute", item?.title ?? "");
                          sendClickLog("REMOVE_SLOTS_CREATE_START_MINUTE", item?.title ?? "Start Minute");
                        }}
                        fieldError={formHook.formState.errors.startTimeMinute}
                        register={formHook.register("startTimeMinute")}
                        selectItems={startTimeMinuteItem}
                        placeholder="MM"
                        showTooltips={false}
                        showTypedErrors={false}
                        showDropdownIcon
                        defaultSelected={startTimeMinuteItem?.find(
                          (item) => item.title === formHook.watch("startTimeMinute"),
                        )}
                        listBoxClassName="w-full z-[70]"
                        className="text-xs text-gray-700"
                        containerClassName="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex-1 space-y-1">
                  <p className="text-xs font-semibold text-[#1F1F23]">End Time</p>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <NonSearchableSelectMenu
                        errorMessages={[{ message: "Hour is required", type: "required" }]}
                        onSelectItem={(item) => {
                          formHook.setValue("endTimeHour", item?.title ?? "");
                          sendClickLog("REMOVE_SLOTS_CREATE_END_HOUR", item?.title ?? "End Hour");
                        }}
                        fieldError={formHook.formState.errors.endTimeHour}
                        register={formHook.register("endTimeHour")}
                        selectItems={startTimeItem}
                        placeholder="HH"
                        showTooltips={false}
                        showTypedErrors={false}
                        showDropdownIcon
                        defaultSelected={startTimeItem?.find((item) => item.title === formHook.watch("endTimeHour"))}
                        listBoxClassName="w-full z-[60]"
                        className="text-xs text-gray-700"
                        containerClassName="w-full"
                      />
                    </div>
                    <div className="flex-1">
                      <NonSearchableSelectMenu
                        errorMessages={[{ message: "Minute is required", type: "required" }]}
                        onSelectItem={(item) => {
                          formHook.setValue("endTimeMinute", item?.title ?? "");
                          sendClickLog("REMOVE_SLOTS_CREATE_END_MINUTE", item?.title ?? "End Minute");
                        }}
                        fieldError={formHook.formState.errors.endTimeMinute}
                        register={formHook.register("endTimeMinute")}
                        selectItems={startTimeMinuteItem}
                        placeholder="MM"
                        showTooltips={false}
                        showTypedErrors={false}
                        showDropdownIcon
                        defaultSelected={startTimeMinuteItem?.find(
                          (item) => item.title === formHook.watch("endTimeMinute"),
                        )}
                        listBoxClassName="w-full z-[60]"
                        className="text-xs text-gray-700"
                        containerClassName="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FormRow>

          <div className="pt-5 mt-2 border-t border-gray-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                sendClickLog("REMOVE_SLOTS_CREATE_CANCEL", "Cancel");
                onCancel();
              }}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !selectedClinicId || !selectedProviderId}
              className="px-4 py-2 rounded-lg text-xs font-medium bg-black text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
      <OverbookingConfirmationModal
        open={isConfirmModalOpen}
        title="Confirm Slot Removal"
        message={confirmModalMessage}
        confirmText={isSaving ? "Saving..." : "OK"}
        cancelText="Cancel"
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmSave}
        confirmDisabled={isSaving}
        cancelDisabled={isSaving}
      />
    </div>
  );
};

const SubServicesList = ({ subServices }: { subServices: any[] }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!subServices || subServices.length === 0) {
    return <span>-</span>;
  }

  const hasMore = subServices.length > 5;
  const displayList = subServices.slice(0, 5);

  return (
    <div className="flex flex-col gap-1">
      {displayList.map((s, idx) => (
        <div key={idx} className="text-left text-xs text-gray-800 break-words leading-tight">
          • {s.sub_service_name}
        </div>
      ))}
      {hasMore && (
        <>
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="text-left text-xs text-blue-600 hover:text-blue-800 font-semibold mt-1 focus:outline-none w-fit"
          >
            + {subServices.length - 5} more
          </button>
          
          <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="z-50 fixed inset-0 overflow-y-auto" onClose={() => setIsOpen(false)}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
              </Transition.Child>

              <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="inline-block align-middle bg-white shadow-xl rounded-2xl w-full max-w-sm text-left transform transition-all overflow-hidden p-6">
                    <Dialog.Title as="h3" className="mb-2 font-semibold text-[#000] text-sm flex justify-between items-center">
                      <span>Sub Services ({subServices.length})</span>
                      <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="text-gray-400 hover:text-gray-600 text-sm font-bold focus:outline-none"
                      >
                        ✕
                      </button>
                    </Dialog.Title>
                    <hr className="mb-4" />
                    <div className="flex flex-col gap-2 max-h-[40vh] overflow-y-auto pr-1 custom-scrollbar">
                      {subServices.map((s, idx) => (
                        <div key={idx} className="text-left text-xs text-gray-800 break-words leading-normal">
                          • {s.sub_service_name}
                        </div>
                      ))}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition.Root>
        </>
      )}
    </div>
  );
};

const RemoveSlotsComponent = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { NO_PWL_RESULTS } = useImageConstants();
  const { veticDateFormat, truncateString } = useUtils();

  const { execute: logAPI } = useClickLogAPI();
  const loggedInUser = useAppSelector((state) => state.auth?.user);
  const vmsUserId = loggedInUser?.email ?? "";
  const userId = loggedInUser?.id ?? "";

  const sendClickLog = (eventType: string, ctaText: string) => {
    logAPI({
      event_type: eventType,
      scr_id: "REMOVE_SLOTS",
      timestamp: Date.now(),
      user_id: userId,
      vms_user_id: vmsUserId,
      cta_text: ctaText,
    });
  };

  const routeId = getRouteKey("ROTA", "id");
  const { routeState } = useRouteInfo(routeId);

  const clinics = routeState?.state?.clinics ?? [];
  const provider = routeState?.state?.provider ?? [];

  const fetchCities = useOrgCitiesListAPI("ROTA");
  const { execute: fetchClinicStaff, loading: isStaffLoading } = useRotaSlotRemovalsClinicStaffAPI();
  const { execute: fetchSlotRemovals, loading: isApiLoading } = useRotaSlotRemovalsListAPI();
  const { execute: uploadBulkFile, loading: isUploadLoading } = useRotaSlotRemovalsBulkUploadAPI();

  const [slotsList, setSlotsList] = useState<IRotaSlotRemovalData[]>([]);
  const [clinicStaffList, setClinicStaffList] = useState<IRotaSlotRemovalClinicStaffData[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<IRotaSlotRemovalData | null>(null);
  const [deleteImpactCount, setDeleteImpactCount] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const formHook = useForm<IRemoveSlotsFormFields>({
    mode: "onChange",
    defaultValues: EMPTY_FILTERS,
  });

  const clinicItems = useSelectMenuReducer(clinics, "display_name", "id");
  const providerItems = useSelectMenuReducer(clinicStaffList, "staff_name", "staff_id");

  const selectedClinicId = formHook.watch("clinicId");
  const selectedClinicName = formHook.watch("clinicName");
  const selectedProviderId = formHook.watch("providerId");
  const selectedProviderName = formHook.watch("providerName");
  const selectedDate = formHook.watch("date");

  useEffect(() => {
    fetchCities({
      withClinics: true,
      withClinicProviders: false,
      clinicOptions: { selectAll: true },
    });
  }, []);

  useEffect(() => {
    formHook.setValue("providerId", "");
    formHook.setValue("providerName", "");
    setSlotsList([]);
    setHasSearched(false);
    setClinicStaffList([]);

    if (!selectedClinicId) {
      return;
    }

    fetchClinicStaff({ clinic_id: selectedClinicId }).then((data) => {
      setClinicStaffList(data);
    });
  }, [selectedClinicId]);

  useEffect(() => {
    if (!selectedClinicId || selectedClinicName) return;
    const matchedClinic = clinicItems?.find((item) => item.id === selectedClinicId);
    if (matchedClinic?.title) {
      formHook.setValue("clinicName", matchedClinic.title, { shouldValidate: true });
    }
  }, [selectedClinicId, clinicItems]);

  const handleApplyFilters = () => {
    sendClickLog("REMOVE_SLOTS_APPLY_FILTERS", "Apply Filters");
    if (!selectedClinicId) {
      toast.error("Please select a clinic");
      return;
    }

    setHasSearched(true);
    fetchSlotRemovals({
      clinic_id: selectedClinicId,
      staff_id: selectedProviderId,
      date: selectedDate,
    }).then((data) => {
      setSlotsList(data);
    });
  };

  const handleResetFilters = () => {
    sendClickLog("REMOVE_SLOTS_CLEAR_FILTERS", "Clear Filters");
    formHook.reset(EMPTY_FILTERS);
    setSlotsList([]);
    setHasSearched(false);
  };

  const confirmDeleteSlot = () => {
    sendClickLog("REMOVE_SLOTS_CONFIRM_REMOVE", "Remove");
    if (!selectedSlot) return;
    setIsDeleting(true);

    setTimeout(() => {
      setSlotsList((prev) =>
        prev.filter(
          (s) =>
            !(
              s.staff_id === selectedSlot.staff_id &&
              s.clinic_id === selectedSlot.clinic_id &&
              s.date === selectedSlot.date &&
              s.start_time === selectedSlot.start_time &&
              s.end_time === selectedSlot.end_time
            )
        )
      );
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setSelectedSlot(null);
      setDeleteImpactCount(null);
      toast.success("Schedule/slots successfully removed");
    }, 500);
  };

  const handleUploadClick = () => {
    sendClickLog("REMOVE_SLOTS_UPLOAD_CLICK", "Upload");
    uploadInputRef.current?.click();
  };

  const handleUploadFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;

    const lowerName = file.name?.toLowerCase?.() || "";
    const isValidExcelFile = lowerName.endsWith(".xls") || lowerName.endsWith(".xlsx");
    if (!isValidExcelFile) {
      toast.error("Please upload a valid .xls or .xlsx file.");
      event.target.value = "";
      return;
    }

    const toastId = toast.loading("Uploading slot removal file...");
    try {
      const response = await uploadBulkFile(file);
      toast.dismiss(toastId);
      if (response && response.status === true) {
        if (selectedClinicId) {
          fetchSlotRemovals({
            clinic_id: selectedClinicId,
            staff_id: selectedProviderId,
            date: selectedDate,
          }).then((data) => {
            setSlotsList(data);
            setHasSearched(true);
          });
        }
      }
    } catch (error) {
      toast.dismiss(toastId);
    } finally {
      event.target.value = "";
    }
  };

  const handleSampleFileDownload = () => {
    sendClickLog("REMOVE_SLOTS_DOWNLOAD_SAMPLE", "Download Sample File");
    const link = document.createElement("a");
    link.href = "/remove_slots_sample_file.xlsx";
    link.download = "remove_slots_sample_file.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Sample file download started");
  };

  const handleSampleSheetOpen = () => {
    sendClickLog("REMOVE_SLOTS_OPEN_SAMPLE_SHEET", "Open Sample Sheet");
    window.open(
      "https://docs.google.com/spreadsheets/d/1OTCKfWy28kTAZudS_FjqGQSmaEAbWYoGVxt6FGxf-GM/edit?gid=624088451#gid=624088451",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const shouldShowClearFilters = [selectedClinicId, selectedProviderId, selectedDate].some(
    (value) => `${value ?? ""}`.trim().length > 0,
  );

  return (
    <div className="px-0 py-0">
      <input
        ref={uploadInputRef}
        type="file"
        accept=".xls,.xlsx"
        className="hidden"
        onChange={handleUploadFileChange}
      />
      <div className="relative z-30 bg-white rounded-2xl border border-gray-100 shadow-sm p-2">
        <div className="flex sm:flex-row flex-col sm:items-start justify-between gap-3">
          <div className="flex-1 flex items-center gap-2">
            <h2 className="font-semibold text-sm text-left">Remove Slots</h2>
            <button
              type="button"
              onClick={handleSampleFileDownload}
              title="Download sample file"
              aria-label="Download sample file"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleSampleSheetOpen}
              title="Open sample sheet"
              aria-label="Open sample sheet"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            >
              <LinkIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="flex sm:flex-row flex-col items-stretch sm:items-center sm:justify-end gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleUploadClick}
              disabled={isUploadLoading}
              className="w-full sm:w-[120px] py-2 rounded-lg bg-[#F1F1F1] text-[#1F1F23] text-[12px] font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploadLoading ? "Uploading..." : "Upload"}
            </button>
            <button
              type="button"
              onClick={() => {
                sendClickLog("REMOVE_SLOTS_NEW_CLICK", "New");
                navigate(`${getRouteKey("REMOVE_SLOTS", "url")}/create`);
              }}
              className="w-full sm:w-[120px] py-2 rounded-lg bg-black text-white text-[12px] font-medium hover:opacity-90 transition-opacity"
            >
              New
            </button>
          </div>
        </div>

        <div className="mt-3 py-1 rounded-xl w-full">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 w-full">
            <div className="w-full sm:w-[200px] min-w-0">
              <input hidden {...formHook.register("clinicId")} />
              <SearchableSelectMenu
                errorMessages={[{ message: "Clinic is required", type: "required" }]}
                onSelectItem={(item) => {
                  formHook.setValue("clinicId", item?.id ?? "");
                  formHook.setValue("clinicName", item?.title ?? "");
                  sendClickLog("REMOVE_SLOTS_SELECT_CLINIC", item?.title ?? "Select Clinic");
                }}
                fieldError={formHook.formState.errors.clinicName}
                register={formHook.register("clinicName", { required: false })}
                selectItems={clinicItems}
                placeholder="Select Clinic"
                showTooltips={false}
                showTypedErrors={false}
                showDropdownIcon
                defaultSelected={clinicItems?.find((item) => item.id === formHook.watch("clinicId"))}
                listBoxClassName="absolute mt-1 overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none max-h-60 sm:text-sm w-full z-[80]"
                className="text-sm text-gray-800"
                containerClassName="w-full"
                showDropdownWhenNoData
              />
            </div>

            <div className="w-full sm:w-[200px] min-w-0">
              <input hidden {...formHook.register("providerId")} />
              <SearchableSelectMenu
                errorMessages={[{ message: "Provider is required", type: "required" }]}
                onSelectItem={(item) => {
                  formHook.setValue("providerId", item?.id ?? "");
                  formHook.setValue("providerName", item?.title ?? "");
                  sendClickLog("REMOVE_SLOTS_SELECT_PROVIDER", item?.title ?? "Select Provider");
                }}
                fieldError={formHook.formState.errors.providerName}
                register={formHook.register("providerName", { required: false })}
                selectItems={providerItems}
                placeholder={
                  isStaffLoading
                    ? "Loading Providers..."
                    : selectedClinicId
                      ? "Select Provider"
                      : "Please select clinic first"
                }
                showTooltips={false}
                showTypedErrors={false}
                showDropdownIcon
                defaultSelected={providerItems?.find((item) => item.id === formHook.watch("providerId"))}
                listBoxClassName="absolute mt-1 overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none max-h-60 sm:text-sm w-full z-[80]"
                className="text-sm text-gray-800"
                containerClassName="w-full"
                showDropdownWhenNoData
              />
            </div>

            <div className="w-full sm:w-[200px] min-w-0 [&>div]:w-full [&>div>div]:w-full">
              <PWLCalendarComponent
                errorMessages={[{ message: "Date is required", type: "required" }]}
                register={formHook.register("date", { required: false })}
                fieldError={formHook.formState.errors.date}
                showTypedErrors={false}
                allowPastDaySelection={true}
                defaultSelectedDate={formHook.watch("date")}
                onSelectDate={(date) => {
                  if (!date) return;
                  const formattedDate = format(date, "yyyy-MM-dd");
                  formHook.setValue("date", formattedDate);
                  sendClickLog("REMOVE_SLOTS_SELECT_DATE", formattedDate);
                }}
                className="w-full h-9 !py-0 text-sm leading-tight focus:outline-none !focus:ring-0 !ring-gray-300"
              />
            </div>

            <div className="w-full sm:w-auto min-w-0 flex items-center gap-2">
              <button
                type="button"
                onClick={handleApplyFilters}
                className="w-full sm:w-[120px] h-9 rounded-lg bg-black text-white text-[12px] font-medium hover:opacity-90 transition-opacity"
              >
                Apply Filters
              </button>
              {shouldShowClearFilters && (
                <button
                  type="button"
                  title="Clear Filters"
                  aria-label="Clear Filters"
                  onClick={handleResetFilters}
                  className="h-9 w-9 shrink-0 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors flex items-center justify-center"
                >
                  <FunnelX className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 relative z-0">
        {isApiLoading ? (
          <div className="w-full bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px] min-h-[220px] flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mb-2"></div>
            <p className="text-xs text-gray-500">Fetching active shifts...</p>
          </div>
        ) : hasSearched && slotsList.length > 0 ? (
          <div className="rounded-2xl overflow-x-auto">
            <table className="table-auto w-full border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
              <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-10">
                <tr>
                  {TABLE_HEADERS.map((key, index) => (
                    <th
                      key={index}
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-black capitalize tracking-wider whitespace-nowrap"
                    >
                      {key.item}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {slotsList.map((slot, index, arr) => {
                  const isFirst = index === 0;
                  const isLast = index === arr.length - 1;
                  const isSingle = arr.length === 1;

                  const clinicObj = clinics?.find((c: any) => c.id === slot.clinic_id);
                  const clinicName = clinicObj?.display_name || clinicObj?.name || selectedClinicName || slot.clinic_id || "-";

                  const providerObj = clinicStaffList?.find((p) => p.staff_id === slot.staff_id);
                  const staffName = providerObj?.staff_name || provider?.find((p: any) => p.staff_id === slot.staff_id)?.name || selectedProviderName || slot.staff_id || "-";

                  const slotKey = `${slot.staff_id}-${slot.clinic_id}-${slot.date}-${slot.start_time}-${slot.end_time}-${index}`;

                  return (
                    <tr key={slotKey} className="bg-white">
                      {/* Date */}
                      <td
                        className={`px-3 py-3 text-xs font-normal text-black align-middle whitespace-nowrap ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""
                          } ${isFirst && !isSingle ? "rounded-tl-2xl" : ""} ${isLast && !isSingle ? "rounded-bl-2xl" : ""}`}
                      >
                        {veticDateFormat(slot.date) || "-"}
                      </td>

                      {/* Clinic */}
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle whitespace-nowrap">
                        <p className="truncate" title={clinicName}>
                          {truncateString(clinicName, 28)}
                        </p>
                      </td>

                      {/* Provider */}
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle min-w-[140px] max-w-[200px]">
                        <p className="truncate" title={staffName}>
                          {truncateString(staffName, 24)}
                        </p>
                      </td>

                      {/* Subservices */}
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle min-w-[140px] max-w-[250px]">
                        <SubServicesList subServices={slot.sub_service_details || []} />
                      </td>

                      {/* Start Time */}
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle whitespace-nowrap font-mono">
                        {slot.start_time || "-"}
                      </td>

                      {/* End Time */}
                      <td className="px-3 py-3 text-xs font-normal text-black align-middle whitespace-nowrap font-mono">
                        {slot.end_time || "-"}
                      </td>

                      {/* Created By */}
                      <td
                        className={`px-3 py-3 text-xs font-normal text-black align-middle whitespace-nowrap ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""
                          } ${isFirst && !isSingle ? "rounded-tr-2xl" : ""} ${isLast && !isSingle ? "rounded-br-2xl" : ""}`}
                      >
                        {slot.removed_by || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-2xl overflow-x-auto">
            <div className="w-full bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
              <div className="bg-white rounded-2xl min-h-[220px] flex items-center justify-center">
                <NoRecordFoundComponent
                  imageURL={NO_PWL_RESULTS}
                  message={hasSearched ? "No Rota Slots Found" : "Apply Filters to View Slots"}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <OverbookingConfirmationModal
        open={isDeleteModalOpen}
        title="Confirm Slot Removal"
        message="Are you sure you want to proceed ahead with slot removal? This action cannot be undone."
        impactPrefix="Removing this rota shift will impact"
        impactCount={deleteImpactCount}
        confirmText={isDeleting ? "Removing..." : "Remove"}
        confirmButtonClassName="inline-flex justify-center bg-[#E11D48] px-6 py-2 rounded-md w-full sm:w-auto font-semibold text-white text-xs disabled:opacity-50"
        onClose={() => {
          if (isDeleting) return;
          sendClickLog("REMOVE_SLOTS_CANCEL_REMOVE", "Cancel");
          setIsDeleteModalOpen(false);
          setSelectedSlot(null);
          setDeleteImpactCount(null);
        }}
        onConfirm={confirmDeleteSlot}
        confirmDisabled={isDeleting}
      />
    </div>
  );
};

export default RemoveSlotsComponent;
